import { OrganizationResponseDto } from "@/lib/interfaces/organizations";

export interface SettingsTabContext {
  organization: OrganizationResponseDto;
  isSuperAdmin: boolean;
}

export interface SettingsTabDefinition {
  /** Stable, human readable address of the tab. Appears as ?tab=<id>. */
  id: string;
  label: string;
  /** Absent means always visible. */
  isVisible?: (context: SettingsTabContext) => boolean;
}

/**
 * The tabs of the settings page, in display order.
 *
 * Addressed by id rather than by position. With positions, a conditional tab shifted
 * every tab behind it: "Conversion-Tracking" sat on ?tab=9 for an organization without
 * Kandidat KI and on ?tab=10 for one with it, so the same page had a different address
 * per customer and a shared link led somewhere else. Adding or removing a tab moved
 * everything behind it as well.
 */
export const SETTINGS_TABS: SettingsTabDefinition[] = [
  { id: "unternehmen", label: "Personalisierung/Unternehmen" },
  { id: "email-signatur", label: "Email-Signatur" },
  { id: "schnittstellen", label: "Schnittstellen" },
  { id: "rollen", label: "Rollen und Rechte" },
  { id: "datenschutz", label: "Datenschutzerklärung" },
  { id: "agentur", label: "Agentur" },
  { id: "abo", label: "Abo" },
  { id: "benachrichtigungen", label: "Interne Benachrichtigung" },
  { id: "kandidat-ki", label: "Kandidat KI", isVisible: ({ organization }) => Boolean(organization.aiEnabled) },
  { id: "conversion", label: "Conversion-Tracking", isVisible: ({ isSuperAdmin }) => isSuperAdmin },
  { id: "webhooks", label: "Webhooks", isVisible: ({ isSuperAdmin }) => isSuperAdmin },
];

/**
 * The tab order as it was before the switch to named ids, so a link someone saved or
 * pasted into a chat still opens what it used to open.
 *
 * Position 9 was "Kandidat KI" for organizations that have it and "Conversion-Tracking"
 * for those that do not. That ambiguity cannot be resolved after the fact — it is the
 * very reason for this change — so it resolves to the more common case.
 *
 * Can be deleted once old links have stopped appearing; nothing else depends on it.
 */
const LEGACY_TAB_POSITIONS = [
  "unternehmen",
  "email-signatur",
  "schnittstellen",
  "rollen",
  "datenschutz",
  "agentur",
  "abo",
  "benachrichtigungen",
  "kandidat-ki",
  "conversion",
];

export const getVisibleSettingsTabs = (context: SettingsTabContext): SettingsTabDefinition[] =>
  SETTINGS_TABS.filter((tab) => !tab.isVisible || tab.isVisible(context));

/**
 * Resolves whatever stands in the address to a tab that this user can actually see.
 *
 * Anything unknown, empty or hidden falls back to the first visible tab rather than
 * showing an empty page — the same forgiving behaviour the numeric version had.
 */
export const resolveSettingsTabId = (tabParam: string | null, visibleTabs: SettingsTabDefinition[]): string => {
  const firstVisibleId = visibleTabs[0]?.id ?? SETTINGS_TABS[0].id;
  if (!tabParam) return firstVisibleId;

  if (visibleTabs.some((tab) => tab.id === tabParam)) return tabParam;

  const legacyPosition = Number(tabParam);
  if (Number.isInteger(legacyPosition) && legacyPosition >= 1 && legacyPosition <= LEGACY_TAB_POSITIONS.length) {
    const legacyId = LEGACY_TAB_POSITIONS[legacyPosition - 1];
    if (visibleTabs.some((tab) => tab.id === legacyId)) return legacyId;
  }

  return firstVisibleId;
};
